import {
  ApiError,
  isRecord,
  json,
  nullableString,
  requestAnonymous,
  requireString,
} from '@/lib/api'
import type { components } from '@/lib/openapi.generated'
import type { Replace } from '@/lib/type-utils'

type Schemas = components['schemas']
type LoginFlowResponse = Schemas['LoginFlowResponse']
type CompletePasswordLoginResponse = Schemas['CompletePasswordLoginResponse']
type ResolveLoginResponse = Replace<Schemas['ResolveLoginResponse'], 'method', {
  method: 'password' | 'redirect'
}>

/**
 * Decodes the safe flow projection that the route loader reads.
 * This response does not expose the authorization request ID or callback state.
 */
function decodeLoginFlow(raw: unknown): LoginFlowResponse {
  if (!isRecord(raw)) throw new Error('Malformed login flow response')
  return {
    loginHint: nullableString(raw.loginHint, 'loginHint'),
  } satisfies LoginFlowResponse
}

/**
 * Decodes the local callback URL that the Host returns after a password login.
 * The login route checks this URL before it sends the browser to the OIDC callback.
 */
function decodeCompletePasswordLogin(raw: unknown): CompletePasswordLoginResponse {
  if (!isRecord(raw)) throw new Error('Malformed password login response')
  return {
    callbackUrl: requireString(raw.callbackUrl, 'callbackUrl'),
  } satisfies CompletePasswordLoginResponse
}

/**
 * Decodes the login method that the Host selects.
 * Provider selection stays on the server, so the portal does not contain provider IDs or routing rules.
 */
function decodeResolveLogin(raw: unknown): ResolveLoginResponse {
  if (!isRecord(raw)) throw new Error('Malformed login route response')
  const method = requireString(raw.method, 'method')
  if (method !== 'password' && method !== 'redirect')
    throw new Error('Malformed login route response')
  return {
    method,
    redirectUrl: nullableString(raw.redirectUrl, 'redirectUrl'),
  } satisfies ResolveLoginResponse
}

/**
 * Returns a message that the login page can show.
 * API errors use the controlled Problem Details message from the Host.
 */
export function interactiveLoginErrorMessage(cause: unknown): string {
  if (cause instanceof ApiError)
    return cause.problem?.detail ?? 'Sign-in is temporarily unavailable.'
  return cause instanceof Error
    ? cause.message
    : 'Sign-in is temporarily unavailable.'
}

/** Tells the password form when its server-side flow is gone and it must offer a fresh sign-in. */
export function isLoginFlowExpired(cause: unknown): boolean {
  return (
    cause instanceof ApiError && cause.problem?.code === 'LOGIN_FLOW_EXPIRED'
  )
}

/**
 * The portal starts a standard OIDC authorization request with Zitadel.
 * Zitadel sends the browser to the Host login entry instead of showing the Zitadel user interface.
 * The Host stores authorization state in Redis and selects a native password or external provider.
 * A native password step stays inside the portal.
 * An external account goes directly to its upstream identity provider and returns to the Host.
 * The Host uses the Zitadel Login API to finalize OIDC and return the browser to the platform callback.
 *
 * These operations are anonymous because no platform identity exists before the OIDC callback is complete.
 */
export const interactiveLoginApi = {
  /**
   * Reads the safe flow projection that the HttpOnly cookie selects.
   * This request does not acquire or advance the flow.
   */
  readFlow: () =>
    requestAnonymous('/api/v1/auth/login/flow', decodeLoginFlow),

  /**
   * Completes a native password step through the Host.
   * The Host sends the password to the Zitadel Session API and returns the local OIDC callback.
   */
  completePassword: (loginName: string, password: string) =>
    requestAnonymous(
      '/api/v1/auth/login/password',
      decodeCompletePasswordLogin,
      json(
        'POST',
        { loginName, password } satisfies Schemas['CompletePasswordLoginRequest'],
      ),
    ),

  /**
   * Requests the server-owned routing decision for a login name.
   * A redirect response contains the upstream provider URL, not a Zitadel login page.
   */
  resolve: (loginName: string) =>
    requestAnonymous(
      '/api/v1/auth/login/route',
      decodeResolveLogin,
      json('POST', { loginName } satisfies Schemas['ResolveLoginRequest']),
    ),
}
